import React, {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react';
import {
  Close, 
  Fullscreen,
} from '@material-ui/icons';
import { useLocation, useHistory } from 'react-router-dom';
import { pathsToAvoidByAssistantIcon } from 'utils/paths';
import PropTypes from 'prop-types';
import MessagesCard from 'views/Settings/Assistant/MessagesCard';
import {
  ASSISTANT_ID,
  OK_STATUS,
  STATUS,
} from 'views/Settings/Assistant/constants';
import Dynamicore, { SERVICES } from 'services/dynamicore';
import Form from 'views/Settings/Assistant/Form';
import { dynamicSort } from 'utils/arrays';
import { getCurrentSessionData } from 'utils/aws/cognito';
import { LinearProgress, IconButton } from '@material-ui/core';

const AssistantContext = createContext();

export const useAssistant = () => {
  return useContext(AssistantContext);
};

function AssistantWrapper({ children }) {
  const messagesEndRef = useRef(null);
  const { pathname: path } = useLocation();
  const [thread, setThread] = useState();
  const [message, setMessage] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);

  const history = useHistory();

  const normalizeMessages = (messages = []) =>
    (Array.isArray(messages) ? messages : [])
      .map((item) => ({
        ...item,
        content: Array.isArray(item?.content)
          ? item.content
          : typeof item?.content === 'string'
            ? [{
              type: 'text',
              text: {
                value: item.content,
              },
            }]
            : [],
      }))
      .sort(dynamicSort('created_at'));

  const hasPendingAssistantReply = (messages = []) => {
    const lastMessage = [...messages].pop();
    return lastMessage?.role === 'user';
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const contextValue = {
    setShowChat,
    message,
    setMessage,
    file,
    setFile,
    thread,
    setThread,
    isLoading,
    setIsLoading,
    sendMessage: async (replaceMessage = null) => {
      const trimmedMessage = String(replaceMessage ?? message ?? '').trim();
      const hasFile = !!file;
      const strMessage = trimmedMessage || (hasFile ? 'Analiza la imagen adjunta y usala como contexto' : '');
      if (!strMessage && !hasFile) {
        console.error('no message available');
        return;
      }
      setIsLoading(true);

      if (strMessage) {
        const messageContent = [
          {
            type: 'text',
            text: {
              value: strMessage,
            },
          },
        ];

        if (file) {
          messageContent.push({
            type: 'file',
            value: {
              name: file.name,
              url: file.url,
              rawUrl: file.rawUrl,
              preview: file.preview,
              mimeType: file.mimeType,
              s3Path: file.s3Path,
              s3Uri: file.s3Uri,
            },
          });
        }

        setThread((prev) => {
          const newThread = {
            ...prev,
            messages: [
              ...(prev?.messages || []),
              {
                content: messageContent,
                created_at: new Date().getTime() / 1000,
                role: 'user',
              },
              {
                content: [],
                created_at: new Date().getTime() / 1000,
                role: 'assistant',
              },
            ],
            status: STATUS.SENDING,
          };
          return newThread;
        });
      }

      try {
        if (hasFile && file?.s3Path) {
          const filePayload = {
            thread_id: thread?.id,
            content: strMessage,
            s3_path: file?.s3Uri || file?.s3Path,
          };
          console.debug('[assistant] files payload', filePayload);
          const response = await Dynamicore(
            SERVICES.ASSISTANT_CREATE_MESSAGE_WITH_FILE,
            {
              assistantId: ASSISTANT_ID,
            },
          ).post(filePayload);
          if (
            response?.name === 'StatusCodeError' ||
            Number(response?.statusCode) >= 400 ||
            Number(response?.error?.code) >= 400
          ) {
            throw new Error(response?.message || 'Error attaching file to assistant message');
          }
        } else {
          await Dynamicore(SERVICES.ASSISTANT_CREATE_MESSAGE).post({
            thread_id: thread?.id,
            content: strMessage,
          });
        }
        setFile(null);
        await refreshMessages(thread?.id);

        if (replaceMessage === null) {
          setMessage('');
        }
      } catch (error) {
        if (hasFile && file?.url) {
          try {
            const fallbackPayload = {
              thread_id: thread?.id,
              content: `${strMessage}\n\nArchivo adjunto: ${file.url}`,
            };
            console.debug('[assistant] fallback payload', fallbackPayload);
            await Dynamicore(SERVICES.ASSISTANT_CREATE_MESSAGE).post({
              ...fallbackPayload,
            });
            setFile(null);
            await refreshMessages(thread?.id);
            if (replaceMessage === null) {
              setMessage('');
            }
            return;
          } catch (fallbackError) {
            console.error(fallbackError);
          }
        }

        if (strMessage.trim()) {
          setThread((prev) => ({
            ...prev,
            messages: prev.messages.slice(0, -2),
            status: STATUS.ERROR,
          }));
        }
      } finally {
        setIsLoading(false);
      }
    },
    async handleCleanThread() {
      try {
        await Dynamicore(SERVICES.ASSISTANT_DELETE_THREAD, {
          threadId: thread?.id,
        }).delete({}, true);
        localStorage.removeItem('thread_id');
        setThread(null);
        loadData();
      } catch (error) {
        localStorage.removeItem('thread_id');
        setThread(null);
        loadData();
      }
    },
  };

  const getMessages = async function (threadId) {
    const { values = [] } = await Dynamicore(SERVICES.ASSISTANT_READ_MESSAGE, {
      threadId,
    }).get();

    const messages = normalizeMessages(values);
    const status = hasPendingAssistantReply(messages) ? 'in_progress' : STATUS.COMPLETED;

    if (status === STATUS.COMPLETED) {
      setMessage('');
    }

    return {
      messages: messages.length ? messages : thread?.messages ?? [],
      status,
    };
  };

  const pollMessages = async function (threadId, {
    attempts = 10,
    delayMs = 1500,
  } = {}) {
    let response = await getMessages(threadId);

    for (let i = 0; i < attempts; i += 1) {
      const lastMessage = [...(response?.messages ?? [])].pop();
      const hasAssistantReply = lastMessage?.role === 'assistant'
        && (lastMessage?.content ?? []).length > 0;

      if (hasAssistantReply || response?.status === STATUS.COMPLETED) {
        return response;
      }

      await sleep(delayMs);
      response = await getMessages(threadId);
    }

    return response;
  };

  const refreshMessages = async function (threadId) {
    if (!threadId) return;

    const response = await pollMessages(threadId);

    setThread((prev) => ({
      ...(prev || {}),
      id: threadId,
      lastQuery: new Date(),
      messages: response.messages,
      status: response.status,
    }));
  };

  const loadData = async function () {
    try {
      let data = {};
      const savedThread = localStorage.getItem('thread_id');

      if (savedThread) {
        data.id = savedThread;

        const response = await getMessages(savedThread);

        data.lastQuery = new Date();
        data.messages = response.messages;
        data.status = response.status;
      } else {
        data = await Dynamicore(SERVICES.ASSISTANT_THREAD).post();

        if (!data?.id) {
          throw new Error();
        }

        localStorage.setItem('thread_id', data?.id);

        data.lastQuery = new Date();
        data.messages = [];
        data.status = STATUS.OK;
      }

      const { userData } = getCurrentSessionData();

      data.user = {
        ...userData?.UserAttributes,
        username: userData?.Username,
      };

      const lastMessage = [...(data?.messages ?? [])].pop();
      if (lastMessage?.role === 'user') {
        data.messages.push({
          content: [],
          created_at: new Date().getTime() / 1000,
          role: 'assistant',
        });
      }

      setThread(data);
    } catch (error) {
      setThread({
        ...(thread || {}),
        error: error.message,
        lastQuery: new Date().getTime() / 1000,
        status: 'ERROR',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showChat && (!thread || !OK_STATUS.includes(thread?.status))) {
      loadData();
    }
    if (showChat && messagesEndRef.current) {
      const timer = setTimeout(() => {
        messagesEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showChat]);

  useEffect(() => {
    if (!showChat || !messagesEndRef.current) return;

    const timer = setTimeout(() => {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 60);

    return () => clearTimeout(timer);
  }, [showChat, thread?.messages?.length]);

  const handleFullscreen = () => {
    history.push('/settings/assistant');
  };

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
      {!pathsToAvoidByAssistantIcon.some((p) => p === path) && (
        <>
          {showChat && (
            <div
              style={{
                position: 'absolute',
                right: '20px',
                bottom: '20px',
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid #db1c1f',
                backgroundColor: 'white',
                Width: '400px',
                maxWidth: '400px',
                zIndex: 120,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                }}
              >
                <IconButton onClick={handleFullscreen} size='small'>
                  <Fullscreen />
                </IconButton>

                <IconButton onClick={() => setShowChat(false)} size='small'>
                  <Close />
                </IconButton>
              </div>
              {!thread && <LinearProgress />}
              <MessagesCard
                messagesEndRef={messagesEndRef}
                sendMessage={contextValue.sendMessage}
                setMessage={setMessage}
                thread={thread}
              />
              <Form
                isLoading={isLoading}
                message={message}
                setMessage={setMessage}
                sendMessage={contextValue.sendMessage}
                thread={thread}
                file={file}
                setFile={setFile}
              />
            </div>
          )}
        </>
      )}
    </AssistantContext.Provider>
  );
}

export default AssistantWrapper;

AssistantWrapper.propTypes = {
  children: PropTypes.any,
};
